import type { GameState, Piece } from '@sheshdash/shared';
import { decodeJWTPayload } from './jwt';

export const TOKENS_STORAGE_KEY = 'sheshdash_tokens';
export const LAST_JOIN_STORAGE_KEY = 'sheshdash_last_join';
const REFRESH_TIMEOUT_MS = 3000;

/**
 * Build the WebSocket URL from current window location.
 * Supports `?ws=` override for E2E/debugging.
 */
export function getWebSocketUrl(): string {
  const host = window.location.host;
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';

  const params = new URLSearchParams(window.location.search);
  const override = params.get('ws');
  if (override) {
    return override;
  }

  const wsParams = new URLSearchParams({ v: '1', platform: 'web' });

  try {
    const raw = localStorage.getItem(TOKENS_STORAGE_KEY);
    if (raw) {
      const tokens = JSON.parse(raw);
      const accessToken = tokens?.access_token;
      const refreshToken = tokens?.refresh_token;
      const payload = accessToken ? decodeJWTPayload(accessToken) : null;
      const nowSec = Math.floor(Date.now() / 1000);
      const isAccessTokenUsable = Boolean(
        accessToken &&
        payload?.exp &&
        payload.exp - nowSec > 30,
      );

      if (isAccessTokenUsable) {
        wsParams.set('token', tokens.access_token);
      } else if (accessToken && !refreshToken) {
        // Broken token pair can lock WS in endless 401 reconnect loop.
        localStorage.removeItem(TOKENS_STORAGE_KEY);
      }
    }
  } catch {
    // ignore parse errors
  }

  return `${protocol}//${host}/ws?${wsParams.toString()}`;
}

/**
 * Refresh the access token if it's expired or about to expire (within 60s).
 * Updates localStorage on success, clears tokens on failure.
 */
export async function refreshAccessTokenIfNeeded(): Promise<void> {
  let refreshTimeout: number | undefined;
  try {
    const raw = localStorage.getItem(TOKENS_STORAGE_KEY);
    if (!raw) return;
    const tokens = JSON.parse(raw);
    if (!tokens?.access_token) return;
    if (!tokens?.refresh_token) {
      localStorage.removeItem(TOKENS_STORAGE_KEY);
      return;
    }

    const payload = decodeJWTPayload(tokens.access_token);
    if (!payload?.exp) {
      localStorage.removeItem(TOKENS_STORAGE_KEY);
      return;
    }
    if (payload?.exp) {
      const nowSec = Math.floor(Date.now() / 1000);
      if (payload.exp - nowSec > 60) return;
    }

    const controller = new AbortController();
    refreshTimeout = window.setTimeout(() => controller.abort(), REFRESH_TIMEOUT_MS);

    const resp = await fetch('/api/v1/auth/refresh', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      signal: controller.signal,
      body: JSON.stringify({ refresh_token: tokens.refresh_token }),
    });

    if (!resp.ok) {
      localStorage.removeItem(TOKENS_STORAGE_KEY);
      return;
    }

    const newTokens = await resp.json();
    localStorage.setItem(TOKENS_STORAGE_KEY, JSON.stringify(newTokens));
  } catch {
    // Network/timeout error — proceed with existing (possibly expired) token
  } finally {
    if (refreshTimeout) {
      window.clearTimeout(refreshTimeout);
    }
  }
}

/**
 * Parse `?forceDice=6,3,1` query param into a number array.
 */
export function getForceDiceQueue(): number[] {
  const params = new URLSearchParams(window.location.search);
  const value = params.get('forceDice');
  if (!value) return [];
  const parts = value.split(',').map((part) => Number(part.trim()));
  return parts.filter((num) => Number.isInteger(num) && num >= 1 && num <= 6);
}

export interface LastJoin {
  gameId: string;
  playerName: string;
  reconnectToken?: string;
}

/**
 * Load reconnect info from sessionStorage.
 */
export function loadLastJoin(): LastJoin | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(LAST_JOIN_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { gameId?: string; playerName?: string; reconnectToken?: string };
    if (!parsed?.gameId || !parsed?.playerName) return null;
    return {
      gameId: parsed.gameId,
      playerName: parsed.playerName,
      reconnectToken: parsed.reconnectToken,
    };
  } catch {
    return null;
  }
}

/**
 * Save reconnect info to sessionStorage.
 */
export function saveLastJoin(value: LastJoin | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (!value) {
      window.sessionStorage.removeItem(LAST_JOIN_STORAGE_KEY);
      return;
    }
    window.sessionStorage.setItem(LAST_JOIN_STORAGE_KEY, JSON.stringify(value));
  } catch {
    // ignore storage errors
  }
}

/**
 * Find the most relevant piece at a given board position.
 * Priority: own on-board > own reserve > any on-board > any reserve.
 */
export function getPieceAtPosition(
  gameState: GameState | null,
  playerId: string | null,
  row: number,
  col: number,
): Piece | null {
  if (!gameState) return null;

  let ownedReserve: Piece | null = null;
  let ownedOnBoard: Piece | null = null;
  let anyOnBoard: Piece | null = null;
  let anyReserve: Piece | null = null;

  for (const player of gameState.players) {
    for (const piece of player.pieces) {
      if (piece.position.row !== row || piece.position.col !== col) {
        continue;
      }

      const isOwned = playerId && piece.owner_id === playerId;
      const isOnBoard = piece.state !== 'in_corner';

      if (isOwned) {
        if (isOnBoard) {
          ownedOnBoard = piece;
        } else if (!ownedReserve) {
          ownedReserve = piece;
        }
      } else if (isOnBoard && !anyOnBoard) {
        anyOnBoard = piece;
      } else if (!isOnBoard && !anyReserve) {
        anyReserve = piece;
      }
    }
  }

  return ownedOnBoard ?? ownedReserve ?? anyOnBoard ?? anyReserve;
}
